import { zodResolver } from "@hookform/resolvers/zod";
import { ChevronDown, ChevronUp, Save, X } from "lucide-react";
import type React from "react";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import FieldError from "../components/FieldError";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import ValidationBanner from "../components/ValidationBanner";
import { useAuth } from "../context/AuthContext";
import { useWallets } from "../context/WalletContext";
import {
	ASSETS,
	CHAINS,
	CUSTODIAL_TYPES,
	MAU_RANGES,
	PRODUCTS,
	WISHLIST_STATUSES,
} from "../lib/constants";
import { type EditWalletFormData, editWalletSchema } from "../schemas/wallet";
import {
	type Asset,
	type Chain,
	COUNTRIES,
	type CustodialType,
	type MAURange,
	type Product,
	type WishlistStatus,
} from "../types";
import { notifications } from "../utils/notifications";

const selectClass =
	"flex h-9 w-full rounded-(--radius-sm) border border-(--color-input) bg-(--color-card) px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-(--color-ring)";

const EditWallet: React.FC = () => {
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const { isAdmin } = useAuth();
	const { getWallet, updateWallet } = useWallets();
	const bannerRef = useRef<HTMLDivElement>(null);
	const [showOptionalFields, setShowOptionalFields] = useState(false);
	const [marketSearch, setMarketSearch] = useState("");

	const form = useForm<EditWalletFormData>({
		resolver: zodResolver(editWalletSchema) as any,
		defaultValues: {
			walletName: "",
			companyName: "",
			selectedProducts: [] as Product[],
			custodialType: "Custodial" as CustodialType,
			chain: "Stellar" as Chain,
			asset: "USDC" as Asset,
			contactName: "",
			contactEmail: "",
			contactPhone: "",
			contactRole: "",
			primaryContactEmail: "",
			slackChannel: "",
			homeDomain: "",
			fundingAddress: "",
			agentId: "",
			mainOfficeId: "",
			conductsKYC: undefined as boolean | undefined,
			mau: undefined as MAURange | undefined,
			licenses: "",
			countryOfIncorporation: "",
			selectedWishlist: [] as WishlistStatus[],
			selectedMarkets: [] as string[],
		},
		mode: "onSubmit",
	});

	const wallet = id ? getWallet(id) : undefined;

	const selectedProducts = form.watch("selectedProducts") ?? [];
	const selectedWishlist = form.watch("selectedWishlist") ?? [];
	const selectedMarkets = form.watch("selectedMarkets") ?? [];
	const errors = form.formState.errors;
	const hasErrors = Object.keys(errors).length > 0;
	const errorMessages = hasErrors
		? Object.values(errors)
				.filter((e): e is { message?: string } => !!e && typeof e === "object")
				.map((e) => e.message)
				.filter((m): m is string => !!m)
		: [];

	// biome-ignore lint/correctness/useExhaustiveDependencies: intentional primitive deps to reduce effect re-runs
	useEffect(() => {
		if (wallet) {
			form.reset({
				walletName: wallet.wallet_name,
				companyName: wallet.company_name,
				selectedProducts: wallet.products ?? [],
				custodialType: wallet.custodial_type,
				chain: wallet.chain,
				asset: wallet.asset,
				contactName: wallet.main_contact_name,
				contactEmail: wallet.main_contact_email,
				contactPhone: wallet.main_contact_phone,
				contactRole: wallet.main_contact_role,
				primaryContactEmail: wallet.main_contact_email || "",
				slackChannel: wallet.slack_channel || "",
				homeDomain: wallet.home_domain || "",
				fundingAddress: wallet.funding_address || "",
				agentId: wallet.agent_id || "",
				mainOfficeId: wallet.main_office_id || "",
				conductsKYC: wallet.conducts_kyc,
				mau: wallet.mau != null ? (String(wallet.mau) as MAURange) : undefined,
				licenses: (wallet as any).licenses || "",
				countryOfIncorporation: wallet.country_of_incorporation || "",
				selectedWishlist: wallet.whitelist_environments ?? [],
				selectedMarkets: wallet.priority_markets ?? [],
			});

			if (
				(wallet.whitelist_environments?.length ?? 0) > 0 ||
				wallet.conducts_kyc !== undefined ||
				wallet.mau ||
				(wallet as any).licenses ||
				wallet.country_of_incorporation ||
				wallet.priority_markets?.length ||
				wallet.slack_channel ||
				wallet.home_domain ||
				wallet.funding_address ||
				wallet.agent_id ||
				wallet.main_office_id
			) {
				setShowOptionalFields(true);
			}
		}
	}, [wallet?.id, wallet?.updated_at, form]);

	if (!wallet) {
		return (
			<div className="min-h-[calc(100vh-56px)] bg-(--color-background) px-4 pt-20 pb-8">
				<div className="mx-auto max-w-[1000px]">
					<Card className="p-6">
						<h2>Wallet not found</h2>
						<Button
							type="button"
							variant="outline"
							className="mt-4"
							onClick={() => navigate("/")}
						>
							Back to Dashboard
						</Button>
					</Card>
				</div>
			</div>
		);
	}

	const handleProductToggle = (product: Product) => {
		const current = form.getValues("selectedProducts") ?? [];
		const next = current.includes(product)
			? current.filter((p) => p !== product)
			: [...current, product];
		form.setValue("selectedProducts", next);
	};

	const handleWishlistToggle = (status: WishlistStatus) => {
		const current = form.getValues("selectedWishlist") ?? [];
		const next = current.includes(status)
			? current.filter((s) => s !== status)
			: [...current, status];
		form.setValue("selectedWishlist", next);
	};

	const handleMarketSelect = (country: string) => {
		const current = form.getValues("selectedMarkets") ?? [];
		if (!current.includes(country)) {
			form.setValue("selectedMarkets", [...current, country]);
		}
		setMarketSearch("");
	};

	const handleMarketRemove = (country: string) => {
		const current = form.getValues("selectedMarkets") ?? [];
		form.setValue(
			"selectedMarkets",
			current.filter((c) => c !== country),
		);
	};

	const handleSelectAllMarkets = () => {
		form.setValue("selectedMarkets", COUNTRIES);
	};

	const filteredCountries = COUNTRIES.filter(
		(country) =>
			country.toLowerCase().includes(marketSearch.toLowerCase()) &&
			!selectedMarkets.includes(country),
	);

	const onValidSubmit = async (data: EditWalletFormData) => {
		try {
			await updateWallet(wallet!.id, {
				wallet_name: data.walletName,
				company_name: data.companyName,
				custodial_type: (data.custodialType as CustodialType) ?? "Custodial",
				chain: (data.chain as Chain) ?? "Stellar",
				asset: (data.asset as Asset) ?? "USDC",
				products: data.selectedProducts ?? [],
				main_contact_name: data.contactName ?? "",
				main_contact_email: data.contactEmail ?? "",
				main_contact_phone: data.contactPhone ?? "",
				main_contact_role: data.contactRole ?? "",
				slack_channel: data.slackChannel,
				home_domain: data.homeDomain,
				funding_address: data.fundingAddress,
				agent_id: data.agentId,
				main_office_id: data.mainOfficeId,
				whitelist_environments: (data.selectedWishlist ??
					[]) as WishlistStatus[],
				conducts_kyc:
					data.conductsKYC != null ? Boolean(data.conductsKYC) : undefined,
				mau: data.mau as MAURange | undefined,
				country_of_incorporation: data.countryOfIncorporation,
				priority_markets:
					(data.selectedMarkets?.length ?? 0) > 0
						? data.selectedMarkets
						: undefined,
			});
			if (!isAdmin) {
				notifications.success(
					"Your changes have been submitted for admin approval. You will be notified when they are reviewed.",
				);
			}
			navigate(`/wallet/${wallet!.id}`);
		} catch {
			// Error already set in context / shown by caller
		}
	};

	const onInvalid = () => {
		bannerRef.current?.scrollIntoView({ behavior: "smooth" });
	};

	return (
		<div className="min-h-[calc(100vh-56px)] overflow-x-hidden bg-(--color-background) px-4 pt-20 pb-8">
			<div className="mx-auto max-w-[1000px]">
				<div className="mb-8">
					<h1 className="mb-2 text-(--color-foreground) normal-case font-bold text-2xl md:text-3xl">
						Edit Wallet
					</h1>
					<p className="text-(--color-muted-foreground) text-sm">
						Update wallet information
					</p>
				</div>

				<form
					onSubmit={form.handleSubmit(onValidSubmit as any, onInvalid)}
					className="flex flex-col gap-6"
				>
					<div ref={bannerRef}>
						{hasErrors && (
							<ValidationBanner
								message="Please fix the errors below before submitting."
								fields={errorMessages}
								aria-live="polite"
							/>
						)}
					</div>

					{/* Basic Information */}
					<Card className="p-6">
						<h2 className="mb-5 font-semibold text-xl">Basic Information</h2>
						<div className="grid grid-cols-1 gap-5 md:grid-cols-2">
							<div className="space-y-1.5">
								<Label htmlFor="walletName">
									Wallet Name{" "}
									<span className="text-(--color-error-500)">*</span>
								</Label>
								<Input
									type="text"
									id="walletName"
									{...form.register("walletName")}
									className={
										errors.walletName ? "border-(--color-error-500)" : undefined
									}
									aria-invalid={!!errors.walletName}
									aria-describedby={
										errors.walletName ? "walletName-error" : undefined
									}
								/>
								{errors.walletName?.message && (
									<FieldError
										id="walletName-error"
										message={String(errors.walletName.message)}
									/>
								)}
							</div>

							<div className="space-y-1.5">
								<Label htmlFor="companyName">
									Company Name{" "}
									<span className="text-(--color-error-500)">*</span>
								</Label>
								<Input
									type="text"
									id="companyName"
									{...form.register("companyName")}
									className={
										errors.companyName
											? "border-(--color-error-500)"
											: undefined
									}
									aria-invalid={!!errors.companyName}
									aria-describedby={
										errors.companyName ? "companyName-error" : undefined
									}
								/>
								{errors.companyName?.message && (
									<FieldError
										id="companyName-error"
										message={String(errors.companyName.message)}
									/>
								)}
							</div>

							<div className="space-y-1.5">
								<Label htmlFor="custodialType">
									Custodial Type{" "}
									<span className="text-(--color-error-500)">*</span>
								</Label>
								<select
									id="custodialType"
									{...form.register("custodialType")}
									className={selectClass}
								>
									{CUSTODIAL_TYPES.map((t) => (
										<option key={t} value={t}>
											{t}
										</option>
									))}
								</select>
							</div>

							<div className="space-y-1.5">
								<Label htmlFor="chain">
									Chain <span className="text-(--color-error-500)">*</span>
								</Label>
								<select
									id="chain"
									{...form.register("chain")}
									className={selectClass}
								>
									{CHAINS.map((c) => (
										<option key={c} value={c}>
											{c}
										</option>
									))}
								</select>
							</div>

							<div className="space-y-1.5">
								<Label htmlFor="asset">
									Asset <span className="text-(--color-error-500)">*</span>
								</Label>
								<select
									id="asset"
									{...form.register("asset")}
									className={selectClass}
								>
									{ASSETS.map((a) => (
										<option key={a} value={a}>
											{a}
										</option>
									))}
								</select>
							</div>

							<div className="col-span-full space-y-1.5">
								{/* biome-ignore lint/a11y/noLabelWithoutControl: Checkbox group label */}
								<Label>
									Products <span className="text-(--color-error-500)">*</span>
								</Label>
								<div className="flex flex-wrap gap-6">
									{PRODUCTS.map((product) => (
										<label
											key={product}
											className="flex cursor-pointer items-center gap-2"
										>
											<input
												type="checkbox"
												checked={selectedProducts.includes(product)}
												onChange={() => handleProductToggle(product)}
												className="h-4 w-4 rounded border-(--color-input) accent-(--color-primary-500)"
											/>
											<span className="font-medium text-sm">{product}</span>
										</label>
									))}
								</div>
								{errors.selectedProducts?.message && (
									<FieldError
										message={String(errors.selectedProducts.message)}
									/>
								)}
							</div>
						</div>
					</Card>

					{/* Main Contact */}
					<Card className="p-6">
						<h2 className="mb-5 font-semibold text-xl">Main Contact</h2>
						<div className="grid grid-cols-1 gap-5 md:grid-cols-2">
							<div className="space-y-1.5">
								<Label htmlFor="contactName">Name</Label>
								<Input
									type="text"
									id="contactName"
									{...form.register("contactName")}
								/>
							</div>

							<div className="space-y-1.5">
								<Label htmlFor="contactEmail">Email</Label>
								<Input
									type="email"
									id="contactEmail"
									{...form.register("contactEmail")}
								/>
							</div>

							<div className="space-y-1.5">
								<Label htmlFor="contactPhone">Phone Number</Label>
								<Input
									type="tel"
									id="contactPhone"
									{...form.register("contactPhone")}
								/>
							</div>

							<div className="space-y-1.5">
								<Label htmlFor="contactRole">Role</Label>
								<Input
									type="text"
									id="contactRole"
									{...form.register("contactRole")}
									placeholder="Optional"
								/>
							</div>

							<div className="space-y-1.5">
								<Label htmlFor="primaryContactEmail">
									Primary Contact Email
								</Label>
								<Input
									type="email"
									id="primaryContactEmail"
									{...form.register("primaryContactEmail")}
									placeholder="Primary email for wallet"
								/>
							</div>
						</div>
					</Card>

					{/* Optional Fields */}
					<Card className="p-6">
						<button
							type="button"
							className="mb-5 flex w-full items-center justify-between rounded-(--radius-sm) bg-(--color-muted) px-4 py-3 font-semibold text-(--color-foreground) text-sm uppercase tracking-wide transition-colors hover:bg-(--color-neutral-200)"
							onClick={() => setShowOptionalFields(!showOptionalFields)}
						>
							<span>Optional Fields</span>
							{showOptionalFields ? (
								<ChevronUp size={20} />
							) : (
								<ChevronDown size={20} />
							)}
						</button>

						{showOptionalFields && (
							<div className="space-y-6">
								{/* Technical Info */}
								<div>
									<h3 className="mb-3 font-semibold text-(--color-foreground) text-sm uppercase tracking-wide">
										Technical Information
									</h3>
									<div className="grid grid-cols-1 gap-5 md:grid-cols-2">
										<div className="space-y-1.5">
											<Label htmlFor="slackChannel">Slack Channel</Label>
											<Input
												type="text"
												id="slackChannel"
												{...form.register("slackChannel")}
												placeholder="e.g., #wallet-integration"
											/>
										</div>

										<div className="space-y-1.5">
											<Label htmlFor="homeDomain">Home Domain</Label>
											<Input
												type="text"
												id="homeDomain"
												{...form.register("homeDomain")}
												placeholder="e.g., wallet.example.com"
											/>
										</div>

										<div className="col-span-full space-y-1.5">
											<Label htmlFor="fundingAddress">Funding Address</Label>
											<Input
												type="text"
												id="fundingAddress"
												{...form.register("fundingAddress")}
												placeholder="Enter blockchain funding address"
											/>
										</div>

										<div className="space-y-1.5">
											<Label htmlFor="agentId">Agent ID</Label>
											<Input
												type="text"
												id="agentId"
												{...form.register("agentId")}
												placeholder="Enter Agent ID"
											/>
										</div>

										<div className="space-y-1.5">
											<Label htmlFor="mainOfficeId">Main Office ID</Label>
											<Input
												type="text"
												id="mainOfficeId"
												{...form.register("mainOfficeId")}
												placeholder="Enter Main Office ID"
											/>
										</div>
									</div>
								</div>

								{/* Whitelist Status */}
								<div>
									<h3 className="mb-3 font-semibold text-(--color-foreground) text-sm uppercase tracking-wide">
										Whitelist Status
									</h3>
									<div className="flex flex-wrap gap-6">
										{WISHLIST_STATUSES.map((status) => (
											<label
												key={status}
												className="flex cursor-pointer items-center gap-2"
											>
												<input
													type="checkbox"
													checked={selectedWishlist.includes(status)}
													onChange={() => handleWishlistToggle(status)}
													className="h-4 w-4 rounded border-(--color-input) accent-(--color-primary-500)"
												/>
												<span className="font-medium text-sm">{status}</span>
											</label>
										))}
									</div>
								</div>

								{/* Additional details */}
								<div className="grid grid-cols-1 gap-5 md:grid-cols-2">
									<div className="space-y-1.5">
										<Label htmlFor="conductsKYC">
											Does the wallet conduct KYC on users?
										</Label>
										<select
											id="conductsKYC"
											{...form.register("conductsKYC")}
											className={selectClass}
										>
											<option value="">Select...</option>
											<option value="true">Yes</option>
											<option value="false">No</option>
										</select>
									</div>

									<div className="space-y-1.5">
										<Label htmlFor="mau">Monthly Active Users (MAU)</Label>
										<select
											id="mau"
											{...form.register("mau")}
											className={selectClass}
										>
											<option value="">Select range...</option>
											{MAU_RANGES.map((r) => (
												<option key={r} value={r}>
													{r}
												</option>
											))}
										</select>
									</div>

									<div className="col-span-full space-y-1.5">
										<Label htmlFor="licenses">Licenses</Label>
										<Textarea
											id="licenses"
											{...form.register("licenses")}
											rows={3}
											placeholder="Enter any relevant licenses..."
										/>
									</div>

									<div className="space-y-1.5">
										<Label htmlFor="countryOfIncorporation">
											Country of Incorporation
										</Label>
										<select
											id="countryOfIncorporation"
											{...form.register("countryOfIncorporation")}
											className={selectClass}
										>
											<option value="">Select country...</option>
											{COUNTRIES.map((country) => (
												<option key={country} value={country}>
													{country}
												</option>
											))}
										</select>
									</div>

									<div className="col-span-full space-y-1.5">
										<Label htmlFor="edit-market-search">
											Priority Markets Served
										</Label>
										<div className="relative">
											<div className="mb-3 flex gap-3">
												<Input
													id="edit-market-search"
													type="text"
													placeholder="Search countries..."
													value={marketSearch}
													onChange={(e) => setMarketSearch(e.target.value)}
													className="flex-1"
												/>
												<Button
													type="button"
													variant="outline"
													size="sm"
													onClick={handleSelectAllMarkets}
												>
													Select All
												</Button>
											</div>

											{marketSearch && filteredCountries.length > 0 && (
												<div className="absolute top-[calc(100%-0.75rem)] right-0 left-0 z-[100] max-h-52 overflow-y-auto rounded-(--radius-sm) border border-(--color-border) bg-(--color-neutral-0) shadow-lg">
													{filteredCountries.slice(0, 10).map((country) => (
														// biome-ignore lint/a11y/useSemanticElements: Dropdown option needs to be a div for styling
														<div
															key={country}
															className="cursor-pointer px-4 py-3 text-sm transition-colors hover:bg-(--color-muted)"
															role="button"
															tabIndex={0}
															onClick={() => handleMarketSelect(country)}
															onKeyDown={(e) => {
																if (e.key === "Enter" || e.key === " ") {
																	e.preventDefault();
																	handleMarketSelect(country);
																}
															}}
														>
															{country}
														</div>
													))}
												</div>
											)}

											{selectedMarkets.length > 0 && (
												<div className="flex min-h-[60px] flex-wrap gap-2 rounded-(--radius-sm) bg-(--color-muted) p-3">
													{selectedMarkets.map((country) => (
														<span
															key={country}
															className="inline-flex items-center gap-1.5 rounded-full border border-(--color-border) bg-(--color-neutral-0) px-3 py-1.5 font-medium text-sm"
														>
															{country}
															<button
																type="button"
																onClick={() => handleMarketRemove(country)}
																className="flex items-center justify-center rounded-full p-0.5 text-(--color-muted-foreground) transition-colors hover:bg-(--color-error-500) hover:text-white"
															>
																<X size={14} />
															</button>
														</span>
													))}
												</div>
											)}
										</div>
									</div>
								</div>
							</div>
						)}
					</Card>

					<div className="flex justify-end gap-4 pt-2">
						<Button
							type="button"
							variant="outline"
							onClick={() => navigate(`/wallet/${wallet.id}`)}
						>
							Cancel
						</Button>
						<Button type="submit">
							<Save size={20} />
							Save Changes
						</Button>
					</div>
				</form>
			</div>
		</div>
	);
};

export default EditWallet;
